import type { ApiResponse } from "@/app/types/api";
import type {
  AuthInviteDto,
  AuthRoleDto,
  AuthSiteDto,
  AuthUserDto,
  UserStatus,
} from "@/app/types/auth";

async function parseResponse<T>(res: Response): Promise<T> {
  const body = (await res.json()) as ApiResponse<T>;
  if (!body.ok) {
    throw new Error(body.code || body.message);
  }
  return body.data;
}

export async function fetchAuthUsers(): Promise<AuthUserDto[]> {
  const res = await fetch("/api/auth/users", { cache: "no-store" });
  return parseResponse<AuthUserDto[]>(res);
}

export async function fetchAuthInvites(): Promise<AuthInviteDto[]> {
  const res = await fetch("/api/auth/invites", { cache: "no-store" });
  return parseResponse<AuthInviteDto[]>(res);
}

export async function createAuthInvite(payload: {
  email: string;
  roleId: number;
  siteIds?: number[];
  siteId?: number;
}) {
  const normalizedSiteIds = Array.isArray(payload.siteIds)
    ? payload.siteIds
    : payload.siteId != null
      ? [payload.siteId]
      : [];
  const res = await fetch("/api/auth/invites", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email: payload.email,
      roleId: payload.roleId,
      siteIds: normalizedSiteIds,
    }),
  });
  return parseResponse<{ inviteId: number; inviteIds?: number[] }>(res);
}

export async function updateAuthUserStatus(payload: {
  userId: number;
  status: UserStatus;
}) {
  const res = await fetch(`/api/auth/users/${payload.userId}/status`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status: payload.status }),
  });
  return parseResponse<AuthUserDto>(res);
}

export async function resendAuthInvite(inviteId: number) {
  const res = await fetch(`/api/auth/invites/${inviteId}/resend`, {
    method: "POST",
  });
  return parseResponse<{ inviteId: number; link?: string | null }>(res);
}

export async function disableAuthUser(userId: number) {
  const res = await fetch(`/api/auth/users/${userId}/disable`, {
    method: "POST",
  });
  return parseResponse<{ userId: number }>(res);
}

export async function deleteAuthUser(userId: number) {
  const res = await fetch(`/api/auth/users/${userId}`, {
    method: "DELETE",
  });
  return parseResponse<{ userId: number }>(res);
}

export async function generateAuthUserResetLink(userId: number) {
  const res = await fetch(`/api/auth/users/${userId}/reset-link`, {
    method: "POST",
  });
  return parseResponse<{ link: string }>(res);
}

export async function requestPasswordReset(email: string) {
  const res = await fetch("/api/auth/password-reset", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  return parseResponse<{ requested: boolean }>(res);
}

export async function fetchAuthSites(): Promise<AuthSiteDto[]> {
  const res = await fetch("/api/auth/sites", { cache: "no-store" });
  return parseResponse<AuthSiteDto[]>(res);
}

export async function createAuthSite(payload: { name: string; code?: string }) {
  const res = await fetch("/api/auth/sites", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return parseResponse<AuthSiteDto>(res);
}

export async function fetchAuthRoles(): Promise<AuthRoleDto[]> {
  const res = await fetch("/api/auth/roles", { cache: "no-store" });
  return parseResponse<AuthRoleDto[]>(res);
}

export async function resolveAuthOrganizationByEmail(email: string) {
  const res = await fetch(
    `/api/auth/organizations/resolve-by-email?email=${encodeURIComponent(email)}`,
    { cache: "no-store" }
  );
  return parseResponse<{ auth0OrgId: string }>(res);
}

export async function resolveAuthOrganization(code: string) {
  const res = await fetch(
    `/api/auth/organizations/resolve?code=${encodeURIComponent(code)}`,
    { cache: "no-store" }
  );
  return parseResponse<{ auth0OrgId: string }>(res);
}
